import type { StateKeys } from "@services/storage/storeTemplate";
import subscriptions from "@services/subscriptions/instance";
import AppointmentDate from "@utils/appointmentDate";
import type { AddressFields, ContactFields } from "@utils/BasicTypes";
import type { CacheTypes } from "@utils/cache/interfaces";
import { validateSideTab } from "@utils/general";
import { sanitize, validateAddress, validateContact } from "@utils/validation";
import { cacheUtils, debounceStorage } from "../..";
import { state, ui } from "../../interfaces/interfaces";
import { stage1Middleware } from "./stage1";
import { bookingMiddleware } from "./stage3/booking/index";
import { serviceAgreementMiddleware } from "./stage3/carePlan/index";
import { chargeMiddleware } from "./stage3/charge";

const FORM_VALIDATION_DELAY = 150;

export const uiProcessor = async (
	action: { payload: { name: string; value: any } },
	next: any,
) => {
	switch (action.payload.name) {
		case "ProblemComboTrade":
		case "ProblemComboCategory":
		case "ProblemInputPostcode":
		case "ProblemComboJob":
		case "ProblemAddressString":
		case "ProblemComboJobSearch":
			stage1Middleware(action, next);
			break;

		case "CarePlanBillingAddressAsProperty":
		case "CarePlanBillingAddressBusinessName":
		case "CarePlanBillingAddressCity":
		case "CarePlanBillingAddressLocality":
		case "CarePlanBillingAddressPostcode":
		case "CarePlanBillingAddressProperty":
		case "CarePlanBillingAddressStreet":
		case "CarePlanBillingContactAsContact":
		case "CarePlanBillingContactEmail":
		case "CarePlanBillingContactFirstName":
		case "CarePlanBillingContactLandline":
		case "CarePlanBillingContactLastName":
		case "CarePlanBillingContactMobile":
		case "CarePlanBillingContactTitle":
		case "CarePlanBillingMethodPaymentType":
		case "CarePlanCustomerAddressAsContact":
		case "CarePlanCustomerAddressAsProperty":
		case "CarePlanCustomerAddressBusinessName":
		case "CarePlanCustomerAddressCity":
		case "CarePlanCustomerAddressLocality":
		case "CarePlanCustomerAddressPostcode":
		case "CarePlanCustomerAddressProperty":
		case "CarePlanCustomerAddressStreet":
		case "CarePlanCustomerContactEmail":
		case "CarePlanCustomerContactFirstName":
		case "CarePlanCustomerContactLandline":
		case "CarePlanCustomerContactLastName":
		case "CarePlanCustomerContactMobile":
		case "CarePlanCustomerContactTitle":
		case "CarePlanDeclinedReason":
		case "CarePlanOffered":
		case "CarePlanPaymentBankDetailsAccountNumber":
		case "CarePlanPaymentBankDetailsBankBranchName":
		case "CarePlanPaymentBankDetailsSortCode":
		case "CarePlanPlanDetailsAddressAsProperty":
		case "CarePlanPlanDetailsAddressBusinessName":
		case "CarePlanPlanDetailsAddressCity":
		case "CarePlanPlanDetailsAddressLocality":
		case "CarePlanPlanDetailsAddressPostcode":
		case "CarePlanPlanDetailsAddressProperty":
		case "CarePlanPlanDetailsAddressStreet":
		case "CarePlanPlanDetailsBoilerBoilerInstallationDate":
		case "CarePlanPlanDetailsBoilerBoilerLastServicedDate":
		case "CarePlanPlanDetailsBoilerBoilerManufacturers":
		case "CarePlanPlanDetailsBoilerBoilerModel":
		case "CarePlanPlanDetailsBoilerGasSafetyDueDate":
		case "CarePlanPlanDetailsBoilerHeatingTypes":
		case "CarePlanPlanDetailsOfferOfferReason":
		case "CarePlanPlanDetailsOfferOfferType":
		case "CarePlanPlanDetailsOfferPromoCode":
		case "CarePlanPlanDetailsPlanOptionsBillingFrequencies":
		case "CarePlanPlanDetailsPlanOptionsBillingFrequency":
		case "CarePlanPlanDetailsPlanOptionsComments":
		case "CarePlanPlanDetailsPlanOptionsDuration":
		case "CarePlanPlanDetailsPlanOptionsExcess":
		case "CarePlanPlanDetailsPlanOptionsPlanType":
		case "CarePlanPlanDetailsPropertyBathrooms":
		case "CarePlanPlanDetailsPropertyBedrooms":
		case "CarePlanPlanDetailsPropertyBuildingTypes":
		case "CarePlanPlanDetailsPropertyComments":
		case "CarePlanPlanDetailsPropertyExclusionDetails":
		case "CarePlanPlanDetailsPropertyExternalDoors":
		case "CarePlanPlanDetailsPropertyGasAppliances":
		case "CarePlanPlanDetailsPropertyRadiators":
		case "CarePlanReference":
			serviceAgreementMiddleware(action, next);
			break;

		case "BookingPaymentAddressAsProperty":
		case "BookingPaymentAddressProperty":
		case "BookingPaymentAddressBusinessName":
		case "BookingPaymentAddressCity":
		case "BookingPaymentAddressCounty":
		case "BookingPaymentAddressLocality":
		case "BookingPaymentAddressPostcode":
		case "BookingPaymentAddressStreet":
		case "BookingPaymentContactAsContact":
		case "BookingPaymentContactEmail":
		case "BookingPaymentContactFirstName":
		case "BookingPaymentContactLandline":
		case "BookingPaymentContactLastName":
		case "BookingPaymentContactMobile":
		case "BookingPaymentContactTitle":
		case "BookingPaymentIsBooked":
		case "BookingPaymentMethodCardAgreed":
		case "BookingPaymentMethodCardSecured":
		case "BookingPaymentMethodCompanyRegistration":
		case "BookingPaymentMethodPaymentMethods":
		case "BookingPaymentMethodPaymentMethodsValid":
		case "BookingPaymentMethodRequisitions":
			bookingMiddleware(action, next);
			break;

		case "RatesRateReason":
		case "RatesDiscount":
		case "PromoCode":
			chargeMiddleware(action, next);
			break;

		case "CallCampaign":
			if (action.payload.value !== null)
				ui.CallAlert = {
					campaign: ui.CallAlert?.campaign || "",
					from: ui.CallAlert?.from || "",
					to: ui.CallAlert?.to || "",
					severity: "success",
					complete: true,
					show: true,
				};
			return next(action);

		case "ActiveStep":
			if (action.payload.value === 1) {
				state.IsFirstStageFailureForbidden = true;
			}
			return next(action);

		case "PropertyAddressSelectedAddress":
			if (action.payload.value !== null) {
				state.IsAddressSelectedOnce = true;
			}
			return next(action);

		case "AppointmentDate":
			if (action.payload.value !== null) {
				const date = AppointmentDate.convertToDate(action.payload.value);
				const isToday = AppointmentDate.checkAppointmentDateIsToday(date);
				const timeRequirementType = AppointmentDate.getTimeRequirementType(
					isToday ? state.Emergency : false,
					date,
				);

				// state.JobTimeRequirementTypeId = timeRequirementType.timeRequirementTypeId;
				state.TimeRequirementType = timeRequirementType;

				state.JobDateTimeRequired = date;
				subscriptions.publish("DateTimeRequired", date);
			}
			return next(action);

		case "RateText":
			return next(action);

		case "AppointmentTime": {
			const appointmentTimeTypes =
				cacheUtils.getCache<CacheTypes.AppointmentTimeTypes>(
					"AppointmentTimeTypes",
				) || [];
			if (appointmentTimeTypes && appointmentTimeTypes.length > 0) {
				const appointmentTimeTypeId = appointmentTimeTypes.find(
					(x: { name: any }) => x.name === action.payload.value.toUpperCase(),
				)?.appointmentTimeTypeId;
				if (appointmentTimeTypeId) {
					state.JobAppointmentTimeTypeId = appointmentTimeTypeId ?? 0;
				} else {
					state.JobAppointmentTimeTypeId = 0;
				}
			}
			ui.RateText = "";
			return next(action);
		}

		case "FailFeedbackValue":
			state.NotesFailed = action.payload.value;
			return next(action);

		case "PropertyAddressIsBusiness":
			state.JobIsCommercial = action.payload.value;
			subscriptions.publish("IsCommercial", action.payload.value);
			return next(action);

		case "RelatedContactFirstName":
		case "RelatedContactLastName":
		case "RelatedContactTitle":
		case "RelatedContactMobile":
		case "RelatedContactLandline":
		case "RelatedContactRelationship": {
			const extractedName = action.payload.name.replace("RelatedContact", "");
			action.payload.value = sanitize(
				action.payload.value,
				extractedName,
				false,
			);
			const validateInputAndForm = () => {
				validateContact(
					"RelatedContact",
					extractedName as ContactFields,
					action.payload.value,
				);
				validateSideTab("RelatedContact");
			};
			debounceStorage.addOrExecute({
				name: action.payload.name,
				delay: FORM_VALIDATION_DELAY,
				callback: validateInputAndForm,
			});
			return next(action);
		}

		case "LandlordAddressProperty":
		case "LandlordAddressStreet":
		case "LandlordAddressLocality":
		case "LandlordAddressPostcode":
		case "LandlordAddressCity":
		case "LandlordAddressCounty":
		case "LandlordAddressBusinessName": {
			const extractedName = action.payload.name.replace("LandlordAddress", "");
			action.payload.value = sanitize(
				action.payload.value,
				extractedName,
				false,
			);
			const validateInputAndForm = () => {
				// validateAddress("LandlordAddress", extractedName as AddressFields, action.payload.value);
				// validateSideTab("LandlordAddress");
			};
			debounceStorage.addOrExecute({
				name: action.payload.name,
				delay: FORM_VALIDATION_DELAY,
				callback: validateInputAndForm,
			});
			return next(action);
		}

		case "LandlordContactFirstName":
		case "LandlordContactLastName":
		case "LandlordContactTitle":
		case "LandlordContactMobile":
		case "LandlordContactLandline":
		case "LandlordContactEmail": {
			const extractedName = action.payload.name.replace("LandlordContact", "");
			action.payload.value = sanitize(
				action.payload.value,
				extractedName,
				false,
			);
			const validateInputAndForm = () => {
				validateContact(
					"LandlordContact",
					extractedName as ContactFields,
					action.payload.value,
				);
				validateSideTab("LandlordContact");
			};
			debounceStorage.addOrExecute({
				name: action.payload.name,
				delay: FORM_VALIDATION_DELAY,
				callback: validateInputAndForm,
			});
			return next(action);
		}

		case "PropertyContactFirstName":
		case "PropertyContactLastName":
		case "PropertyContactTitle":
		case "PropertyContactMobile":
		case "PropertyContactLandline":
		case "PropertyContactEmail":
		case "PropertyAddressProperty":
		case "PropertyAddressStreet":
		case "PropertyAddressLocality":
		case "PropertyAddressPostcode":
		case "PropertyAddressCity":
		case "PropertyAddressCounty":
		case "PropertyAddressBusinessName": {
			const capFirstLetter = (str: string) => {
				return str.charAt(0).toUpperCase() + str.slice(1);
			};
			// useSanitizer is used to sanitize the input value

			const extractedName = action.payload.name
				.replace("PropertyAddress", "")
				.replace("PropertyContact", "");
			if (
				[
					"Property",
					"Street",
					"Locality",
					"Postcode",
					"City",
					"County",
					"BusinessName",
				].find((x) => extractedName.includes(x)) !== undefined
			) {
				action.payload.value = sanitize(
					action.payload.value,
					extractedName,
					false,
				);
				const validateInputAndForm = () => {
					validateAddress(
						"PropertyAddress",
						extractedName as AddressFields,
						action.payload.value,
					);
					validateSideTab("PropertyAddress");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			} else {
				action.payload.value = sanitize(
					action.payload.value,
					extractedName,
					false,
				);
				const validateInputAndForm = () => {
					validateContact(
						"PropertyContact",
						extractedName as ContactFields,
						action.payload.value,
					);
					validateSideTab("PropertyContact");
				};
				debounceStorage.addOrExecute({
					name: action.payload.name,
					delay: FORM_VALIDATION_DELAY,
					callback: validateInputAndForm,
				});
			}

			const name = extractedName
				.toLocaleLowerCase()
				.replace("city", "town")
				.replace("buildingnameornumber", "property")
				.replace("email", "emailAddress")
				.replace("title", "customerTitle")
				.replace("firstname", "customerForename")
				.replace("mobile", "telephoneMobile")
				.replace("landline", "telephoneLandline")
				.replace("lastname", "customerSurname")
				.replace("businessname", "organisation");

			const actionValue = action.payload.value.replaceAll(" ", "");
			state[`Job${capFirstLetter(name)}` as StateKeys] =
				actionValue.length > 0
					? (action.payload.value as never)
					: ("" as never);
			return next(action);
		}

		default:
			return next(action);
	}
};
